import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import * as Yup from "yup";
import {
  Button,
  Form,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Input,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import {
  getInterviewVideoLinkForCallDetails,
  submitInterviewVideoLink,
} from "model/CallModel";

const videoLinkSchema = Yup.object({
  videoLink: Yup.string()
    .trim()
    .required("Video link is required")
    .url("Enter a valid video link"),
});

export const resolveVideoInterviewRowId = (row = {}) =>
  String(row?.id ?? row?.Id ?? "").trim();

export const resolveVideoInterviewCandidateId = (row = {}) =>
  String(row?.id ?? row?.id ?? "").trim();

const extractInterviewVideoLinkFromApiResponse = (apiResponse = {}) => {
  const payload = apiResponse?.data ?? apiResponse ?? {};
  const nested =
    payload?.data && typeof payload.data === "object" ? payload.data : payload;
  return String(
    nested?.videoLink ??
      nested?.videoInterview ??
      nested?.interviewVideoLink ??
      nested?.link ??
      ""
  ).trim();
};

export const resolveVideoInterviewDate = (row = {}) => {
  const raw =
    row?.scheduleDateTime ??
    row?.interviewScheduled ??
    row?.interviewDate ??
    row?.scheduleDateAndTime ??
    "";
  if (raw) {
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString();
    }
  }
  return new Date().toISOString();
};

const VideoInterviewLinkModal = ({
  isOpen,
  onClose,
  row = {},
  onSuccess,
}) => {
  const [videoLink, setVideoLink] = useState("");
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    // console.log('.....row', row)

    const rowId = resolveVideoInterviewRowId(row);
    const fallbackLink = String(row?.videoInterview ?? row?.videoLink ?? "").trim();

    setError("");
    setTouched(false);
    setVideoLink(fallbackLink);

    if (!rowId) return;

    let cancelled = false;
    const loadInterviewVideoLink = async () => {
      setLoading(true);
      try {
        const response = await getInterviewVideoLinkForCallDetails({ id: rowId });
        if (cancelled) return;
        const fetchedLink = extractInterviewVideoLinkFromApiResponse(response);
        if (fetchedLink) {
          setVideoLink(fetchedLink);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadInterviewVideoLink();

    return () => {
      cancelled = true;
    };
  }, [isOpen, row]);

  const validateField = async (value) => {
    try {
      await videoLinkSchema.validateAt("videoLink", { videoLink: value });
      setError("");
      return true;
    } catch (validationError) {
      setError(validationError?.message || "Enter a valid video link");
      return false;
    }
  };

  const handleChange = async (event) => {
    const nextValue = event?.target?.value ?? "";
    setVideoLink(nextValue);
    if (touched) {
      await validateField(nextValue);
    }
  };

  const handleBlur = async () => {
    setTouched(true);
    await validateField(videoLink);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setTouched(true);

    const isValid = await validateField(videoLink);
    if (!isValid) return;

    const candidateClientId = resolveVideoInterviewCandidateId(row);
    if (!candidateClientId) {
      toast.error("Candidate id is missing");
      return;
    }

    setSaving(true);
    try {
      const response = await submitInterviewVideoLink({
        candidateClientId,
        videoLink: String(videoLink).trim(),
        interviewDate: resolveVideoInterviewDate(row),
      });

      if (response?.success || response?.isSuccess || response?.status) {
        toast.success(response?.message || "Video interview link saved");
        onSuccess?.();
        onClose?.();
        return;
      }
      toast.error(response?.message || "Unable to save video interview link");
    } catch (submitError) {
      toast.error(submitError?.message || "Unable to save video interview link");
    } finally {
      setSaving(false);
    }
  };

  const candidateLabel =
    String(row?.candidateName ?? row?.name ?? "").trim() || "Candidate";

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered>
      <ModalHeader toggle={onClose}>Video Interview Link</ModalHeader>
      <ModalBody>
        <p className="text-muted small mb-3">{candidateLabel}</p>
        {loading ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner color="primary" />
          </div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label for="videoInterviewLink">Video Link</Label>
              <Input
                id="videoInterviewLink"
                name="videoLink"
                type="url"
                placeholder="https://example.com/meeting"
                value={videoLink}
                onChange={handleChange}
                onBlur={handleBlur}
                invalid={Boolean(touched && error)}
                disabled={saving}
              />
              {touched && error ? (
                <small className="text-danger d-block mt-1">{error}</small>
              ) : null}
            </FormGroup>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button color="light" type="button" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={saving}>
                {saving ? <Spinner size="sm" /> : "Submit"}
              </Button>
            </div>
          </Form>
        )}
      </ModalBody>
    </Modal>
  );
};

VideoInterviewLinkModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  row: PropTypes.object,
  onSuccess: PropTypes.func,
};

export default VideoInterviewLinkModal;
